import { supabase } from "@/lib/supabase";

const BATCH_SIZE = 100;

const attachAssignees = async (rows) => {
  if (!rows.length) return rows;
  const byLead = new Map();
  const ids = rows.map((row) => row.id).filter(Boolean);
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    const batch = ids.slice(i, i + BATCH_SIZE);
    const { data, error } = await supabase
      .from("lead_assignees")
      .select("lead_id,user_id,added_at,removed_at,profile:profiles!lead_assignees_user_id_fkey(id,full_name,email)")
      .in("lead_id", batch)
      .is("removed_at", null);
    if (error) throw error;
    for (const item of data || []) {
      const list = byLead.get(item.lead_id) || [];
      list.push(item);
      byLead.set(item.lead_id, list);
    }
  }
  return rows.map((row) => ({ ...row, assignees: byLead.get(row.id) || [] }));
};

const fetchAssignedLeadIds = async (userId) => {
  if (!userId) return [];
  const { data, error } = await supabase.from("lead_assignees").select("lead_id").eq("user_id", userId).is("removed_at", null);
  if (error) throw error;
  return (data || []).map((row) => row.lead_id).filter(Boolean);
};

export const fetchLeadSegment = async ({
  segment = "active",
  page = 1,
  pageSize = 100,
  search = "",
  status = "all",
  rm = "all",
  source = "all",
  tag = "all",
  fromDate = "",
  toDate = "",
} = {}) => {
  const safePageSize = Math.min(Math.max(Number(pageSize) || 100, 1), 200);
  const safePage = Math.max(Number(page) || 1, 1);
  const from = (safePage - 1) * safePageSize;
  const to = from + safePageSize - 1;

  let query = supabase
    .from("leads")
    .select("*, assigned_profile:profiles!leads_assigned_to_fkey(id,full_name,email), creator:profiles!leads_created_by_fkey(id,full_name,email), lost_profile:profiles!leads_lost_by_fkey(id,full_name,email)", { count: "exact" })
    .is("deleted_at", null)
    .order("created_at", { ascending: false });

  if (segment === "lost") query = query.eq("status", "lost");
  else query = query.neq("status", "lost");

  if (status && status !== "all") query = query.eq("status", status);
  if (source && source !== "all") query = query.eq("source", source);

  if (tag && tag !== "all") {
    if (tag === "website") query = query.ilike("tag", "%website%").not("tag", "ilike", "%repeat%");
    if (tag === "repeat") query = query.ilike("tag", "%repeat%");
    if (tag === "any-website") query = query.ilike("tag", "%website%");
    if (tag === "none") query = query.or("tag.is.null,tag.eq.");
  }

  if (fromDate) query = query.gte("created_at", `${fromDate}T00:00:00`);
  if (toDate) query = query.lte("created_at", `${toDate}T23:59:59.999`);

  const term = String(search || "").trim();
  if (term) {
    const escaped = term.replace(/,/g, " ").replace(/%/g, "\\%");
    query = query.or(`name.ilike.%${escaped}%,phone.ilike.%${escaped}%,phone_secondary.ilike.%${escaped}%,location.ilike.%${escaped}%,area.ilike.%${escaped}%,pincode.ilike.%${escaped}%`);
  }

  // Preserve the previous User Wise Filter semantics: primary assignment OR co-assignment.
  // Resolve the selected user's co-assigned lead IDs first, then apply the combined filter
  // in the database query so pagination/count stay correct.
  if (rm && rm !== "all") {
    if (rm === "unassigned") {
      // A primary-unassigned lead is the common case. Exclude co-assigned leads on the
      // returned page after the database filter; no lead data is modified by this path.
      query = query.is("assigned_to", null);
    } else {
      const coAssignedIds = await fetchAssignedLeadIds(rm);
      const primaryFilter = `assigned_to.eq.${rm}`;
      if (coAssignedIds.length) {
        const idFilter = coAssignedIds.join(",");
        query = query.or(`${primaryFilter},id.in.(${idFilter})`);
      } else {
        query = query.eq("assigned_to", rm);
      }
    }
  }

  const { data, count, error } = await query.range(from, to);
  if (error) throw error;

  let rows = await attachAssignees(data || []);
  if (rm === "unassigned") rows = rows.filter((row) => !(row.assignees || []).some((a) => Boolean(a?.user_id || a?.profile?.id)));

  return { rows, count: count || 0, page: safePage, pageSize: safePageSize };
};

export const reviveLostLead = async (leadId, userId, nextStatus = "contacted") => {
  if (!leadId) throw new Error("Lead ID is required");
  const allowed = ["new", "contacted", "site_visit", "quotation_given", "negotiation", "not_contacted", "floor_plan_site_info", "estimate_to_be_created", "need_followup"];
  const status = allowed.includes(nextStatus) ? nextStatus : "contacted";
  const { data, error } = await supabase
    .from("leads")
    .update({ status, is_locked: false, updated_at: new Date().toISOString() })
    .eq("id", leadId)
    .eq("status", "lost")
    .select("*")
    .single();
  if (error) throw error;

  if (userId) {
    const { error: activityError } = await supabase.from("lead_activities").insert([{
      lead_id: leadId,
      type: "status_change",
      content: `Lead revived from lost to ${status.replace(/_/g, " ")}`,
      meta: { from_status: "lost", to_status: status, action: "revive" },
      created_by: userId,
    }]);
    if (activityError) throw activityError;
  }
  return data;
};


export const claimLostLead = async (leadId) => {
  if (!leadId) throw new Error("Lead ID is required");
  const { data, error } = await supabase.rpc("claim_lost_lead", { p_lead_id: leadId });
  if (error) throw error;
  return data;
};
